import { type FC, type ReactNode, type Ref } from 'react';

import clsx from 'clsx';
import { type CustomContentProps, useSnackbar } from 'notistack';

import { AlertCircleIcon } from '@/components/common/icons/AlertCircleIcon';
import { CheckmarkCircleIcon } from '@/components/common/icons/CheckmarkCircleIcon';
import { CloseIcon } from '@/components/common/icons/CloseIcon';
import { OverflowingTextTip } from '@/components/common/OverflowingTextTip';
import { isTruthy } from '@/utils/utils';

import { Flex } from '../../../common/Flex';

import styles from './Notification.module.scss';

declare module 'notistack' {
  interface DescriptionPlugin {
    description?: ReactNode;
  }

  interface InternalSnack extends DescriptionPlugin {}

  interface OptionsObject extends DescriptionPlugin {}
}

type NotificationProps = CustomContentProps & {
  ref?: Ref<HTMLDivElement>;
};

export const Notification: FC<NotificationProps> = (props) => {
  const { className, ref, id, variant, message, description, action } = props;

  const { closeSnackbar } = useSnackbar();

  const Icon = variant === 'error' || variant === 'warning' ? AlertCircleIcon : CheckmarkCircleIcon;
  const actionNode = typeof action === 'function' ? action(id) : action;
  const descriptionLineCount = typeof description === 'string' ? 2 : 3;

  return (
    <Flex className={clsx(styles.root, styles[`variant_${variant}`], className)} ref={ref} direction="column" gap={2}>
      <Flex className={styles.title} gap={2} alignItems="center">
        <Icon />

        <OverflowingTextTip type="regular12" lineCount={1} copyToClipboardSupport="overflow">
          {message}
        </OverflowingTextTip>

        <CloseIcon
          className={styles.closeIcon}
          onClick={() => {
            closeSnackbar(id);
          }}
        />
      </Flex>

      {isTruthy(description) && (
        <OverflowingTextTip
          type="regular12"
          lineCount={descriptionLineCount}
          color="white"
          copyToClipboardSupport="overflow"
        >
          {description}
        </OverflowingTextTip>
      )}

      {isTruthy(actionNode) && <div className={styles.actions}>{actionNode}</div>}
    </Flex>
  );
};
